import {pinyin} from 'pinyin-pro';

const DIR_TYPES = ['dir', 'directory', 'folder', 'link-dir', 'remote', 'system'];
const DOC_TYPES = ['file', 'md', 'text', 'html', 'document'];
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf'];
export const MD_EXTS = ['md', 'markdown'];
const TEXT_EXTS = ['txt', 'text'];
export const TEXT_UPLOAD_EXTS = TEXT_EXTS;

export function getNodeChildren(node) {
  if (!node) return [];
  return node.children || node.subs || [];
}

/** 目录 files 原始列表（API 返回文件对象数组，或兼容 URL 字符串） */
export function getDirFileList(dir) {
  const raw = dir && dir.files;
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw;
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
}

/** 将目录 files 转为可在树中展示、可选中的文件节点 */
export function buildDirFileNodes(dir) {
  if (!dir) return [];
  const list = getDirFileList(dir);
  if (!list.length) return [];

  return list.map((file, index) => {
    if (typeof file === 'string') {
      const url = file.trim();
      if (!url) return null;
      const rawName = (url.split('/').pop() || '').split('?')[0];
      let name = rawName;
      try {
        name = decodeURIComponent(rawName);
      } catch (e) {
        name = rawName;
      }
      return {
        id: `file:${dir.id}:${index}`,
        name: name || `文件${index + 1}`,
        type: 'file',
        content: url,
        dir_id: dir.id,
        _isDirFile: true,
      };
    }
    if (!file || typeof file !== 'object') return null;
    if (file.is_deleted === true) return null;
    if (file.id == null || file.id === '') return null;
    return {
      ...file,
      name: file.name || `文件${index + 1}`,
      dir_id: file.dir_id != null ? file.dir_id : dir.id,
      _isDirFile: true,
    };
  }).filter(Boolean);
}

/** 树节点下用于展示的子项：目录附件文件 + 子目录/文档 */
export function getTreeDisplayChildren(node) {
  if (!node) return [];
  let fileNodes = [];
  if (isDirNode(node)) {
    if (node._filteredFiles != null) {
      fileNodes = node._filteredFiles;
    } else {
      fileNodes = buildDirFileNodes(node);
    }
  }
  const childNodes = node._filteredChildren != null
    ? node._filteredChildren
    : getNodeChildren(node);
  return fileNodes.concat(childNodes);
}

export function isDirFileNode(node) {
  return !!(node && node._isDirFile);
}

/** 仅文件节点可被选中（目录 files 列表项，或带 id 的文档叶子） */
export function isSelectableFileNode(node) {
  if (!node || isDirNode(node)) return false;
  if (isDirFileNode(node)) return true;
  return node.id != null && node.id !== '';
}

export function getFileExt(nameOrUrl) {
  if (!nameOrUrl) return '';
  const base = String(nameOrUrl).split('?')[0].split('/').pop() || '';
  const idx = base.lastIndexOf('.');
  if (idx <= 0) return '';
  return base.slice(idx + 1).toLowerCase();
}

export function hasFileExt(name, exts) {
  const ext = getFileExt(name);
  return exts.includes(ext);
}

export function isDirNode(node) {
  if (!node) return false;
  const type = String(node.type || node.node_type || '').toLowerCase();
  if (DIR_TYPES.includes(type)) return true;
  if (DOC_TYPES.includes(type)) return false;
  if (node.is_dir === true || node.isDir === true) return true;
  if (node.is_document === false || node.isDocument === false) return true;
  const children = getNodeChildren(node);
  if (children.length && !node.content && !hasFileExt(node.name, [...OFFICE_EXTS, ...IMAGE_EXTS, ...MD_EXTS, 'txt', 'html'])) {
    return true;
  }
  return false;
}

export function isDocumentNode(node) {
  if (!node || isDirNode(node)) return false;
  const type = String(node.type || node.node_type || '').toLowerCase();
  if (DOC_TYPES.includes(type)) return true;
  if (node.is_document === true || node.isDocument === true) return true;
  if (node.content) return true;
  if (hasFileExt(node.name, [...OFFICE_EXTS, ...IMAGE_EXTS, ...MD_EXTS, 'txt', 'html', 'htm'])) {
    return true;
  }
  return false;
}

export function isMdDocument(node) {
  if (!isDocumentNode(node)) return false;
  const type = String(node.type || '').toLowerCase();
  if (type === 'md' || type === 'markdown') return true;
  if (hasFileExt(node.name, MD_EXTS)) return true;
  if (type === 'text' && hasFileExt(node.name, MD_EXTS)) return true;
  return false;
}

/** 预览页：内容像 Markdown 时按 MD 渲染（含 mermaid / 标题 / 围栏代码块） */
export function looksLikeMarkdownContent(content) {
  if (!content || typeof content !== 'string') return false;
  const s = content.trim();
  if (!s) return false;
  if (/```[\s\S]+?```/.test(s)) return true;
  if (/^#{1,6}\s/m.test(s)) return true;
  if (/!\[[^\]]*\]\([^)]+\)/.test(s)) return true;
  if (/^\s*[-*+]\s/m.test(s)) return true;
  return false;
}

export function isInlineMarkdownContent(node) {
  if (!node) return false;
  const content = node.markdown_content || node.content || '';
  if (!content || typeof content !== 'string') return false;
  if (/^https?:\/\//i.test(content.trim())) return false;
  return isMdDocument(node);
}

/** 编辑器内可在线编辑的文档（text / md） */
export function isEditableDocument(node) {
  if (!node || isDirNode(node)) return false;
  const type = String(node.type || '').toLowerCase();
  if (type === 'md' || type === 'text') return true;
  if (isInlineMarkdownContent(node)) return true;
  return false;
}

export function findDirById(nodes, id) {
  if (id == null || id === '') return null;
  const target = String(id);
  let found = null;
  const walk = (items) => {
    (items || []).some((node) => {
      if (isDirNode(node) && String(node.id) === target) {
        found = node;
        return true;
      }
      return walk(getNodeChildren(node));
    });
  };
  walk(nodes);
  return found;
}

export function isDirIdInTree(nodes, id) {
  return !!findDirById(nodes, id);
}

export function isImageFile(url) {
  return hasFileExt(url, IMAGE_EXTS);
}

export function isPreviewableDocument(url) {
  return hasFileExt(url, OFFICE_EXTS);
}

export function getDocumentUrl(node) {
  if (!node) return '';
  const content = String(node.content || '').trim();
  if (!content) return '';
  const type = String(node.type || '').toLowerCase();
  if (type === 'file') return content;
  if (/^https?:\/\//i.test(content)) return content;
  return '';
}

export function getMarkdownContent(node) {
  if (!node) return '';
  if (node.markdown_content) return node.markdown_content;
  const content = node.content || '';
  if (content && !/^https?:\/\//i.test(content.trim())) return content;
  return '';
}

export function getDocumentTitle(node) {
  if (!node) return '未命名文档';
  if (node.name) return node.name;
  const content = getMarkdownContent(node);
  if (content) {
    const firstLine = content.split('\n').find((line) => line.trim()) || '';
    return firstLine.replace(/^#+\s*/, '').slice(0, 60) || '未命名文档';
  }
  const url = getDocumentUrl(node);
  if (url) {
    return url.split('/').pop() || '未命名文档';
  }
  return '未命名文档';
}

export function flattenDocuments(nodes, list = []) {
  (nodes || []).forEach((node) => {
    if (isDocumentNode(node)) {
      list.push(node);
    }
    flattenDocuments(getNodeChildren(node), list);
  });
  return list;
}

/** 按树顺序收集所有可选文件（目录 files + 文档节点） */
export function flattenSelectableFiles(nodes, list = []) {
  (nodes || []).forEach((node) => {
    if (isDirNode(node)) {
      buildDirFileNodes(node).forEach((f) => list.push(f));
      flattenSelectableFiles(getNodeChildren(node), list);
    } else if (isDocumentNode(node)) {
      list.push(node);
    }
  });
  return list;
}

export function findDocumentById(nodes, id) {
  return findSelectableFileById(nodes, id);
}

export function findSelectableFileById(nodes, id) {
  if (id == null || id === '') return null;
  const target = String(id);
  let found = null;
  const walk = (items) => {
    (items || []).some((node) => {
      if (isDirNode(node)) {
        const hit = buildDirFileNodes(node).find((f) => String(f.id) === target);
        if (hit) {
          found = hit;
          return true;
        }
        return walk(getNodeChildren(node));
      }
      if (isDocumentNode(node) && String(node.id) === target) {
        found = node;
        return true;
      }
      return false;
    });
  };
  walk(nodes);
  return found;
}

export function slugify(text) {
  const slug = String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\u4e00-\u9fa5]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
}

export function extractHeadings(markdown) {
  const headings = [];
  const slugCount = {};
  String(markdown || '').split('\n').forEach((line) => {
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (!match) return;
    let id = slugify(match[2]);
    if (slugCount[id]) {
      slugCount[id] += 1;
      id = `${id}-${slugCount[id]}`;
    } else {
      slugCount[id] = 1;
    }
    headings.push({
      level: match[1].length,
      text: match[2].trim(),
      id,
    });
  });
  return headings;
}

export function getFileIconByExt(ext) {
  const extIconMap = {
    txt: 'md-document-text',
    doc: 'md-document',
    docx: 'md-document',
    pdf: 'md-document',
    xls: 'md-document',
    xlsx: 'md-document',
    ppt: 'md-document',
    pptx: 'md-document',
    md: 'codelibrary-fill',
    markdown: 'codelibrary-fill',
    html: 'css',
    htm: 'css',
    jpg: 'images',
    jpeg: 'images',
    png: 'images',
    gif: 'images',
    svg: 'images',
    webp: 'images',
    bmp: 'images',
  };
  return extIconMap[ext] || 'wenjian';
}

export function getDocumentIcon(node) {
  if (!node) return 'wenjian';
  const type = String(node.type || '').toLowerCase();
  if (type === 'md') return 'codelibrary-fill';
  if (type === 'html') return 'css';
  if (type === 'text') return 'md-document-text';
  const ext = getFileExt(node.name) || getFileExt(getDocumentUrl(node));
  return getFileIconByExt(ext);
}

export function normalizeTreeRoot(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return [data];
}

/** 名称检索键：原文、拼音全拼、拼音首字母 */
export function getNameSearchKeys(name) {
  const raw = String(name || '').trim();
  if (!raw) {
    return {text: '', initials: '', fullPinyin: ''};
  }
  const text = raw.toLowerCase();
  let initials = '';
  let fullPinyin = '';
  try {
    initials = pinyin(raw, {pattern: 'first', toneType: 'none'})
      .replace(/\s/g, '')
      .toLowerCase();
    fullPinyin = pinyin(raw, {toneType: 'none', separator: ''})
      .toLowerCase();
  } catch (e) {
    initials = '';
    fullPinyin = '';
  }
  return {text, initials, fullPinyin};
}

/** 树搜索：支持名称包含、拼音全拼、拼音首字母 */
export function matchesTreeSearchKeyword(name, keyword) {
  const q = String(keyword || '').trim().toLowerCase();
  if (!q) return true;
  const keys = getNameSearchKeys(name);
  if (keys.text.includes(q)) return true;
  if (keys.initials.includes(q)) return true;
  if (keys.fullPinyin.includes(q)) return true;
  return false;
}

export function filterTreeNodes(nodes, keyword) {
  const q = String(keyword || '').trim().toLowerCase();
  if (!q) return nodes;

  const walk = (items) => (items || []).map((node) => {
    if (!node) return null;

    if (isDirNode(node)) {
      const nameMatched = matchesTreeSearchKeyword(node.name, q);
      const allFiles = buildDirFileNodes(node);
      const matchedFiles = nameMatched
        ? allFiles
        : allFiles.filter((f) => matchesTreeSearchKeyword(f.name, q));
      const rawChildren = getNodeChildren(node);
      const filteredChildren = walk(rawChildren);

      if (!nameMatched && !matchedFiles.length && !filteredChildren.length) {
        return null;
      }

      return {
        ...node,
        _filteredFiles: matchedFiles,
        _filteredChildren: nameMatched ? rawChildren : filteredChildren,
      };
    }

    return matchesTreeSearchKeyword(node.name, q) ? node : null;
  }).filter(Boolean);

  return walk(nodes);
}
